import { cartAtom } from "@/atoms"
import { tenantTimezoneAtom } from "@/atoms/tenant-clock"
import { readReferralLanding } from "@/domain/marketing/referral"
import { prizeValidity } from "@/domain/marketing/won-prize"
import { useCouponArrival } from "@/hooks/use-coupon-arrival"
import { useReceivedCoupon, type ReceivedCouponView } from "@/hooks/use-coupon-readiness"
import { getMerchantId } from "@/lib/company-storage"
import { couponBenefitHeadline } from "@/lib/coupon-benefit"
import { undoCouponReplacement } from "@/lib/coupon-arrival"
import { couponNotUsableMessage } from "@/lib/coupon-readiness"
import { couponRevealConditions, couponRevealPending, markCouponRevealed } from "@/lib/coupon-reveal"
import { centsToReais, formatBRL } from "@/lib/format-brl"
import { readCouponQuery } from "@/lib/pending-coupon"
import { useAtomValue } from "jotai"
import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { CouponFreeItemAction } from "./coupon/CouponFreeItemAction"
import { CouponRevealSheet, type CouponRevealContent } from "./CouponRevealSheet"

function contentOf(
  code: string,
  received: ReceivedCouponView,
  referred: boolean,
  timeZone: string,
  replaced: readonly string[],
): CouponRevealContent | null {
  const { readiness, terms } = received
  if (readiness.status === "checking") return null
  if (readiness.status === "invalid" || readiness.status === "unavailable-now") {
    const message = couponNotUsableMessage(code, readiness) ?? `O cupom ${code} não pode ser usado agora.`
    const title = readiness.status === "unavailable-now" ? "Seu cupom vale em outro horário" : "Esse cupom não vale mais"
    return { kind: "not-usable", code, title, message }
  }
  const benefit = terms ? couponBenefitHeadline(terms.benefit, terms.reward, 0, terms.offer) : null
  const cap = terms && terms.reward.kind === "percent" && terms.maxDiscountCents > 0 ? formatBRL(centsToReais(terms.maxDiscountCents)) : null
  return {
    kind: "won",
    code,
    eyebrow: referred ? "Presente de um amigo" : "Cupom liberado",
    headline: benefit?.headline ?? "Desconto especial",
    detail: benefit ? (cap ? `até ${cap} ${benefit.detail}` : benefit.detail) : null,
    conditions: couponRevealConditions(terms, prizeValidity(terms?.expiresAt, timeZone)),
    replaced: [...replaced],
  }
}

function usable(received: ReceivedCouponView | null): boolean {
  const status = received?.readiness.status
  return status === "usable" || status === "unknown"
}

/**
 * Celebrates the coupon a visitor brought in the link (`?cupom=`, referral or
 * campaign) once per code per visit, as soon as its check is in. A usable code
 * first settles against the coupons already on the order (`useCouponArrival`:
 * it replaces them unless coupon-service says they combine) and the sheet says
 * which one it replaced, with "Desfazer". The code is already on the order, so
 * "Usar agora" only closes — straight to the bag when it has items.
 */
export function CouponReveal() {
  const { search } = useLocation()
  const navigate = useNavigate()
  const cart = useAtomValue(cartAtom)
  const timeZone = useAtomValue(tenantTimezoneAtom)
  const referral = readReferralLanding(search)
  const code = referral?.couponCode ?? readCouponQuery(search)
  const received = useReceivedCoupon(code)
  const arrive = useCouponArrival()
  const [closedCode, setClosedCode] = useState<string | null>(null)
  const [arrival, setArrival] = useState<{ code: string; replaced: string[] } | null>(null)
  const merchantId = getMerchantId()
  const pending = Boolean(code && merchantId && couponRevealPending(merchantId, code))
  const needsArrival = pending && usable(received) && arrival?.code !== code

  useEffect(
    function settleArrivedCoupon() {
      if (!needsArrival || !code) return
      let cancelled = false
      void arrive(code).then(({ replaced }) => {
        if (!cancelled) setArrival({ code, replaced })
      })
      return () => {
        cancelled = true
      }
    },
    [needsArrival, code, arrive],
  )

  if (!code || !received || !merchantId) return null
  if (usable(received) && pending && arrival?.code !== code) return null
  const replaced = arrival?.code === code ? arrival.replaced : []
  const content = contentOf(code, received, referral !== null, timeZone, replaced)
  if (!content) return null
  const open = closedCode !== code && pending
  const revealedCode = code
  const revealedMerchant = merchantId

  function close() {
    markCouponRevealed(revealedMerchant, revealedCode)
    setClosedCode(revealedCode)
  }

  function use() {
    close()
    if (cart.items.length > 0) navigate("/cart#cupom")
  }

  function undo() {
    undoCouponReplacement(revealedMerchant, revealedCode, replaced)
    close()
  }

  function itemsAdded(added: boolean) {
    close()
    if (added) navigate("/cart")
  }

  const itemAction = usable(received) ? <CouponFreeItemAction terms={received.terms} tone="game" onDone={itemsAdded} /> : null
  return <CouponRevealSheet open={open} content={content} onUse={use} onBrowse={close} onUndoReplace={undo} itemAction={itemAction} />
}
